import { useWindowSizeStore } from "../store/window-size.store";
import { useMemo } from "react";
import { useDebounce } from "./useDebounce";

type BreakpointsType = "Xs" | "Sm" | "Md" | "Lg" | "Xl" | "Xxl";

const BREAKPOINS: Record<BreakpointsType, number> = {
    Xs: 0,
    Sm: 576,
    Md: 768,
    Lg: 992,
    Xl: 1200,
    Xxl: 1400,
};

type WindowBreakpointsProp = "only" | "down" | "up";
type WindowBreakpointsPrefix = "only" | "isDown" | "isUp";
type ValidationType = { minWidth: number; maxWidth: number };

type BreakpointContent = {
    prefix: WindowBreakpointsPrefix;
    validation: (value: ValidationType) => boolean;
};

type WindowBreakpointsTypes = {
    [key in WindowBreakpointsProp]: BreakpointContent;
};

type Concatenate<Prefix extends string, Suffix extends string> = `${Prefix}${Suffix}`;
type WindowBreakpointsReturn = {
    [P in BreakpointsType as Concatenate<WindowBreakpointsPrefix, BreakpointsType>]: boolean;
};

export const useWindowBreakpoints = () => {
    const { width } = useWindowSizeStore();
    const windowWidth = useDebounce(width, 500);

    const TYPES: WindowBreakpointsTypes = {
        only: {
            prefix: "only",
            validation: ({ minWidth, maxWidth }) => width >= minWidth && width < maxWidth,
        },
        down: {
            prefix: "isDown",
            validation: ({ minWidth }) => minWidth > width,
        },
        up: {
            prefix: "isUp",
            validation: ({ minWidth }) => width >= minWidth,
        },
    };

    const result = useMemo(
        () =>
            Object.keys(TYPES).reduce((breakpoints, breakpointKey) => {
                const currentType = TYPES[breakpointKey as WindowBreakpointsProp];
                const prefix = currentType.prefix;
                const validation = currentType.validation;
                return {
                    ...breakpoints,
                    [prefix + "Xs"]: validation({
                        minWidth: 0,
                        maxWidth: BREAKPOINS.Sm,
                    }),
                    [prefix + "Sm"]: validation({
                        minWidth: BREAKPOINS.Sm,
                        maxWidth: BREAKPOINS.Md,
                    }),
                    [prefix + "Md"]: validation({
                        minWidth: BREAKPOINS.Md,
                        maxWidth: BREAKPOINS.Lg,
                    }),
                    [prefix + "Lg"]: validation({
                        minWidth: BREAKPOINS.Lg,
                        maxWidth: BREAKPOINS.Xl,
                    }),
                    [prefix + "Xl"]: validation({
                        minWidth: BREAKPOINS.Xl,
                        maxWidth: BREAKPOINS.Xxl,
                    }),
                    [prefix + "Xxl"]: validation({
                        minWidth: BREAKPOINS.Xxl,
                        maxWidth: Infinity,
                    }),
                };
            }, {}),
        [windowWidth]
    );

    return result as WindowBreakpointsReturn;
};
